import {
  DocketViewDto,
  MemberTransferDataView,
  MemberTransferReasonDto,
  MemberTransferRequestDto,
} from '@act/shared/data-transfer-objects';
import {
  PayloadAction,
  createAction,
  createEntityAdapter,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import { LoadingStatus } from 'redux/types/shared-types';
import { memberTransferRequests } from '../requests/member-transfer.request';
import { useAppSelector } from '../store-hooks';
import { MemberTransferRequestState } from '../types/memberTransferRequestTypes';
import { createApiThunk } from '../utils/api-thunk';
import { getTodos } from './todoSlice';
import { resetRedux } from '../actions/shared-actions';

// THUNKS/ACTIONS ==============================================================
export const getMemberTransferReasons = createApiThunk(
  memberTransferRequests.getMemberTransferReasons
);

export const createMemberTransferRequest = createApiThunk(
  memberTransferRequests.createMemberTransferRequest
);

export const getMemberTransferRequestsById = createApiThunk(
  memberTransferRequests.getMemberTransferRequestsById
);

export const updateMemberTransferRequest = createApiThunk(
  memberTransferRequests.updateMemberTransferRequest
);

export const getPendingMemberTransferRequestsForAuditor = createApiThunk(
  memberTransferRequests.getPendingMemberTransferRequestsForAuditor
);

export const auditMemberTransferRequest = createApiThunk(
  memberTransferRequests.auditMemberTransferRequest
);

export const auditMemberTransferRequestSuccess =
  createAction<MemberTransferDataView>(
    'memberTransferRequest/auditMemberTransferRequestSuccess'
  );

export const createMemberTransferRequestSuccess =
  createAction<MemberTransferDataView>(
    'memberTransferRequest/createMemberTransferRequestSuccess'
  );
// INITIAL STATE ===============================================================
export const memberTransferRequestAdapter =
  createEntityAdapter<MemberTransferRequestDto>();

const initialState: MemberTransferRequestState = {
  status: 'initial',
  reasonsStatus: 'initial',
  auditIds: [],
  memberTransferReasons: [],
  memberTransferRequests: memberTransferRequestAdapter.getInitialState(),
};

// SLICE =======================================================================
export const memberTransferRequestSlice = createSlice({
  name: 'memberTransferRequest',
  initialState: initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(resetRedux, state => {
        state = { ...initialState };
        return state;
      })
      .addCase(getMemberTransferReasons.pending, state => {
        state.reasonsStatus = 'loading';
      })
      .addCase(
        getMemberTransferReasons.fulfilled,
        (state, action: PayloadAction<MemberTransferReasonDto[]>) => {
          state.memberTransferReasons = action.payload;
          state.reasonsStatus = 'success';
        }
      )
      .addCase(getMemberTransferReasons.rejected, state => {
        state.reasonsStatus = 'failed';
      })
      .addCase(getMemberTransferRequestsById.pending, state => {
        state.status = 'loading';
      })
      .addCase(
        getMemberTransferRequestsById.fulfilled,
        (state, action: PayloadAction<MemberTransferRequestDto[]>) => {
          memberTransferRequestAdapter.addMany(
            state.memberTransferRequests,
            action.payload
          );
          state.status = 'success';
        }
      )
      .addCase(getMemberTransferRequestsById.rejected, state => {
        state.status = 'failed';
      })
      .addCase(
        createMemberTransferRequestSuccess,
        (state, action: PayloadAction<MemberTransferDataView>) => {
          memberTransferRequestAdapter.upsertOne(
            state.memberTransferRequests,
            action.payload.memberTransferRequest
          );
          state.status = 'success';
        }
      )
      .addCase(updateMemberTransferRequest.pending, state => {
        state.status = 'loading';
      })
      .addCase(
        updateMemberTransferRequest.fulfilled,
        (state, action: PayloadAction<MemberTransferRequestDto>) => {
          memberTransferRequestAdapter.upsertOne(
            state.memberTransferRequests,
            action.payload
          );
          state.status = 'success';
        }
      )
      .addCase(updateMemberTransferRequest.rejected, state => {
        state.status = 'failed';
      })
      .addCase(getPendingMemberTransferRequestsForAuditor.pending, state => {
        state.status = 'loading';
      })
      .addCase(
        getPendingMemberTransferRequestsForAuditor.fulfilled,
        (state, action: PayloadAction<MemberTransferRequestDto[]>) => {
          memberTransferRequestAdapter.addMany(
            state.memberTransferRequests,
            action.payload
          );
          state.status = 'success';
        }
      )
      .addCase(getPendingMemberTransferRequestsForAuditor.rejected, state => {
        state.status = 'failed';
      })
      .addCase(getTodos.pending, state => {
        state.status = 'loading';
        state.auditIds = [];
      })
      .addCase(
        getTodos.fulfilled,
        (state, action: PayloadAction<DocketViewDto>) => {
          if (!action.payload.memberTransferRequests) {
            return;
          }
          state.auditIds = action.payload.memberTransferRequests.map(r => r.id);
          memberTransferRequestAdapter.addMany(
            state.memberTransferRequests,
            action.payload.memberTransferRequests
          );
          state.status = 'success';
        }
      )
      .addCase(
        auditMemberTransferRequestSuccess,
        (state, action: PayloadAction<MemberTransferDataView>) => {
          memberTransferRequestAdapter.upsertOne(
            state.memberTransferRequests,
            action.payload.memberTransferRequest
          );
          state.status = 'success';
        }
      )

      // NOTE: addMatcher must come AFTER all addCase calls
      // Listen for getFeedItems.fulfilled without importing getFeedItems
      .addMatcher(
        action => action.type === 'getFeedItems/fulfilled',
        (
          state,
          action: PayloadAction<{
            memberTransferRequests: MemberTransferRequestDto[];
          }>
        ) => {
          memberTransferRequestAdapter.addMany(
            state.memberTransferRequests,
            action.payload.memberTransferRequests
          );
        }
      );
  },
});

// REDUCER =====================================================================
export const memberTransferRequestReducer = memberTransferRequestSlice.reducer;

// SELECTORS ===================================================================
const selectState = (state): MemberTransferRequestState =>
  (state as { memberTransferRequests: MemberTransferRequestState })
    ?.memberTransferRequests;

const selectMemberTransferReasons = createSelector(
  selectState,
  state => state.memberTransferReasons
);

const selectMemberTransferSliceStatuses = () =>
  createSelector(selectState, state => {
    return { status: state.status, reasonsStatus: state.reasonsStatus };
  });

const selectMemberTransferRequests = state =>
  (state as { memberTransferRequests: typeof initialState })
    ?.memberTransferRequests?.memberTransferRequests;

const selectMemberTransferRequestById = (id: number) =>
  createSelector(selectMemberTransferRequests, state => {
    return state.entities[id];
  });

export const selectMemberTransferReasonById = (id: number) =>
  createSelector(selectMemberTransferReasons, reasons => {
    return reasons.find(reason => reason.id === id);
  });

const selectAuditableMemberTransferRequests = createSelector(
  selectState,
  selectMemberTransferRequests,
  (state, adapterState) => {
    return state.auditIds.map(i => adapterState.entities[i]);
  }
);

export const selectPendingMemberTransferRequests = createSelector(
  selectAuditableMemberTransferRequests,
  changeRequests => {
    return changeRequests.filter(
      changeRequest =>
        !changeRequest.deniedAt &&
        !changeRequest.approvedAt &&
        !changeRequest.deletedAt
    );
  }
);

// EXPORT HELPER ===============================================================
export const memberTransferRequestSelectors = {
  useSelectMemberTransferSliceStatuses: (): {
    status: LoadingStatus;
    reasonsStatus: LoadingStatus;
  } => useAppSelector(selectMemberTransferSliceStatuses()),
  useSelectMemberTransferReasons: (): MemberTransferReasonDto[] =>
    useAppSelector(selectMemberTransferReasons),
  useSelectMemberTransferRequestById: (id: number): MemberTransferRequestDto =>
    useAppSelector(selectMemberTransferRequestById(id)),
  useSelectMemberTransferReasonById: (id: number) =>
    useAppSelector(selectMemberTransferReasonById(id)),
  useSelectPendingMemberTransferRequests: (): MemberTransferRequestDto[] =>
    useAppSelector(selectPendingMemberTransferRequests),
};

// EFFECTS =====================================================================
// WEBSOCKET ACTIONS ===========================================================
